import React, { useState, forwardRef } from "react";
import { bool, node, func, string } from "prop-types";
import { twMerge } from "tailwind-merge";

import ArrowLeftIcon from "@components/shared/Icons/ArrowLeft";
import UnderlineLoader from "@components/shared/UnderlineLoader";

const SimpleButton = forwardRef((props, ref) => {
  const {
    children,
    className,
    dataId,
    endAdornment,
    onClick,
    startAdornment,
    withArrow,
    withUnderline,
  } = props;
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    if (isLoading) {
      return;
    }

    try {
      setIsLoading(true);
      await onClick?.();
    } catch (error) {
      throw new Error(
        `Errors of onClick function of SimpleButton must be handled in the onClick handler so that the promise always resolves successfully`
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      className={twMerge(
        "relative flex items-center gap-1 lg:gap-2 p-4 pb-0 my-4",
        "bg-none border-none text-left cursor-pointer transition duration-150",
        "font-citroen font-semibold text-base lowercase focus:outline-none",
        isLoading ? "pointer-events-none opacity-70" : "opacity-100",
        withUnderline && !isLoading ? "underline" : "no-underline",
        "focus-visible:outline-2 focus-visible:outline-brightOrange focus-visible:-outline-offset-[-2px]",
        "hover:no-underline hover:scale-105",
        "active:scale-95",
        className
      )}
      onClick={handleClick}
      ref={ref}
      role="button"
      type="button"
      data-id={dataId}
    >
      {withArrow && <ArrowLeftIcon size="sm" />}
      {startAdornment}
      <span className="relative">
        {children}
        {isLoading && <UnderlineLoader />}
      </span>
      {endAdornment}
    </button>
  );
});

SimpleButton.displayName = "SimpleButton";

SimpleButton.propTypes = {
  children: node.isRequired,
  className: node,
  dataId: string,
  endAdornment: node,
  onClick: func,
  startAdornment: node,
  withArrow: bool,
  withUnderline: bool,
};

export default SimpleButton;
